import { Injectable } from '@angular/core';
import { catchAndContinue, filterUndefined } from 'src/app/lego-utils';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { ISpecificationTableUI } from 'src/app/models';
import { ITablePagination, TaskListHeader } from 'src/app/models/domain';
import { SpecificationDataService } from 'src/app/services';
import { EquipmentFacadeService } from 'src/app/services/equipment-facade.service';
import { CollectionState } from 'src/app/state/collection-state';
import { DesignService } from 'src/app/state/design.service';
import { ItemUiState } from 'src/app/state/item-ui-state';
import { OperationsService } from 'src/app/state/operations';
import { IPage } from 'src/app/state/paged-collection';
import { upsertById } from 'src/app/state/upsert-by-id';
import { handleError } from '../akita-error-helper';

@Injectable({ providedIn: 'root' })
export class ControlSpecificationService {
  private readonly state = new CollectionState<TaskListHeader>();
  private readonly uiState = new ItemUiState<ISpecificationTableUI>();

  public readonly error$ = this.state.error$;
  public readonly uiById$ = this.uiState.byId$;

  /**
   * Not for this domain's own table, which renders the `getPage()` result. EcoService
   * derives change numbers from the headers loaded so far, across both spec domains —
   * the one place the old entity store was genuinely read (`selectAll` with a
   * running-number `filterBy`). Both spec components sit above `em-eco-table` in the
   * Specifications tab, so the rows are present by the time it reads them.
   */
  public readonly taskListHeaders$ = this.state.items$;

  /** The design or equipment the accumulated headers belong to; see `accumulate`. */
  private accumulatedFor?: string;

  public constructor(
    private readonly specificationDataService: SpecificationDataService,
    private readonly operationsService: OperationsService,
    private readonly equipmentFacade: EquipmentFacadeService,
    private readonly designService: DesignService
  ) {}

  public getPage(pagination: ITablePagination): Observable<IPage<TaskListHeader>> {
    // Starting a load clears the previous error, which CustomEntityStore.setLoading(true)
    // used to do. Without it a transient failure sticks: the template renders the error
    // instead of the table, so a later success would never be seen.
    this.state.setError(undefined);

    return this.designService.design$.pipe(
      filterUndefined(() => {
        this.state.reset();
      }),
      switchMap((design) => {
        if (design.materialGroup === '0121') {
          return catchAndContinue(
            this.specificationDataService.getControlSpecificationPageByDesignId(
              design.designId,
              pagination
            ),
            (error) => {
              handleError(this.state, error);
            }
          ).pipe(map((result) => ({ context: design.designId, result })));
        }

        return this.equipmentFacade.selectedEquipmentId$.pipe(
          filterUndefined(() => {
            this.state.reset();
          }),
          switchMap((equipmentNumber) =>
            catchAndContinue(
              this.specificationDataService.getControlSpecificationsByEquipmentNumber(
                equipmentNumber,
                pagination
              ),
              (error) => {
                handleError(this.state, error);
              }
            ).pipe(map((result) => ({ context: equipmentNumber, result })))
          )
        );
      }),
      tap(({ context, result: { data } }) => {
        this.accumulate(context, data);
      }),
      map(({ result: { data, total } }): IPage<TaskListHeader> => ({
        currentPage: pagination.page,
        data,
        lastPage: Math.ceil(total / pagination.perPage),
        perPage: pagination.perPage,
        total,
      }))
    );
  }

  public getAll(): Observable<IPage<TaskListHeader>> {
    this.state.setError(undefined);

    return this.equipmentFacade.selectedEquipmentId$.pipe(
      filterUndefined(() => {
        this.state.reset();
      }),
      switchMap((equipmentNumber) =>
        catchAndContinue(
          this.specificationDataService.getAllControlSpecificationsByEquipmentNumber(
            equipmentNumber
          ),
          (error) => {
            handleError(this.state, error);
          }
        ).pipe(map((result) => ({ context: equipmentNumber, result })))
      ),
      tap(({ context, result: { data } }) => {
        this.accumulate(context, data);
      }),
      // One big page: the "show all" toggle renders everything without a pager.
      map(({ result: { data, total } }): IPage<TaskListHeader> => ({
        currentPage: 1,
        data,
        lastPage: 1,
        perPage: total,
        total,
      }))
    );
  }

  public populateOperationsByTaskListHeader(taskListHeader: TaskListHeader) {
    this.updateUiState(taskListHeader.id, { loading: true, isExpanded: true });

    return this.operationsService.loadByTaskListHeader(taskListHeader).pipe(
      tap(() => {
        this.updateUiState(taskListHeader.id, { loading: false, isExpanded: true });
      })
    );
  }

  public updateUiState(taskListHeaderId: string, ui: ISpecificationTableUI) {
    this.uiState.set(taskListHeaderId, ui);
  }

  /**
   * Mirrors the paginator's `upsertMany`: pages add to the set rather than replacing
   * it, in one emission — Akita batched this in a transaction, and EcoService
   * subscribes to the result.
   *
   * Scoped by context, because accumulating is only correct *within* one design or
   * equipment. `EcoService` matches headers on the last 3 digits of the equipment number
   * (`convertEquipmentNumberToEquipmentRunningNumber` is `slice(-3)`), so headers left
   * over from a previously selected equipment can collide and corrupt the derived ECO
   * change numbers. Akita had the same hole — the store was only emptied when the
   * selection became `undefined`.
   */
  private accumulate(context: string, headers: TaskListHeader[]): void {
    const items = context === this.accumulatedFor ? this.state.items : [];

    this.accumulatedFor = context;
    this.state.set(upsertById(items, headers));
  }
}
